import { useState } from "react";
import { Labeled, useDataProvider, useNotify, useRecordContext, useRefresh, Button } from "react-admin";
import { Box, Checkbox, FormGroup, FormControlLabel, TextField as TextInput } from "@mui/material";
import ModeEditIcon from "@mui/icons-material/ModeEdit";

import { settings } from "app";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { erasmusRequests } from "api/erasmusApi";
import DocumentFileLink from "components/Erasmus/Partials/DocumentFileLink";
import { ErasmusApplicationStatuses } from "utils/constants";

const MESSAGE_MAX_LENGTH = 2000;
const MESSAGE_TOO_LONG_ERROR = `Съобщението не може да е по-дълго от ${MESSAGE_MAX_LENGTH} символа!`;
const MESSAGE_REQUIRED_ERROR = "Моля въведете съобщение!";

const documentFields = record => [
  {
    id: "Cv",
    rawValue: record?.cvDocumentId,
    value: (
      <DocumentFileLink
        id={record?.cvDocumentId}
        documentName={`CV-${record?.cvDocumentId}`}
      />
    ),
    label: "CV",
  },
  {
    id: "MotivationLetter",
    rawValue: record?.motivationLetterDocumentId,
    value: (
      <DocumentFileLink
        id={record?.motivationLetterDocumentId}
        documentName={`MotivationLetter-${record?.motivationLetterDocumentId}`}
      />
    ),
    label: "Мотивационно писмо",
  },
  {
    id: "BankAccountCertificate",
    rawValue: record?.bankAccountCertificateDocumentId,
    value: (
      <DocumentFileLink
        id={record?.bankAccountCertificateDocumentId}
        documentName={`BankAccountCertificate-${record?.bankAccountCertificateDocumentId}`}
      />
    ),
    label: "Удостоверение за банкова сметка",
  },
  {
    id: "Recommendation",
    rawValue: record?.recommendationDocumentId,
    value: (
      <DocumentFileLink
        id={record?.recommendationDocumentId}
        documentName={`Recommendation-${record?.recommendationDocumentId}`}
      />
    ),
    label: "Препоръка",
  },
  {
    id: "LanguageCertificate",
    rawValue: record?.languageCertificateDocumentId,
    value: (
      <DocumentFileLink
        id={record?.languageCertificateDocumentId}
        documentName={`LanguageCertificate-${record?.languageCertificateDocumentId}`}
      />
    ),
    label: "Езиков сертификат",
  },
  {
    id: "AdditionalCertificates",
    value: record?.additionalCertificates,
    label: "Допълнителни сертификати",
    isDocumentObject: true,
  },
];

const ReturnApplicationForCorrectionDialog = () => {
  const record = useRecordContext();

  const [selectedChoices, setSelectedChoices] = useState([]);
  const [message, setMessage] = useState("");
  const [isMessageTouched, setIsMessageTouched] = useState(false);

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record || record.status !== ErasmusApplicationStatuses.Submitted) return null;

  const trimmedMessage = message.trim();
  const isMessageEmpty = !trimmedMessage;
  const isMessageTooLong = message.length > MESSAGE_MAX_LENGTH;
  const isMessageMissing = isMessageTouched && isMessageEmpty;

  const getMessageHelperText = () => {
    if (isMessageTooLong) return MESSAGE_TOO_LONG_ERROR;
    if (isMessageMissing) return MESSAGE_REQUIRED_ERROR;
    return `${message.length}/${MESSAGE_MAX_LENGTH}`;
  };

  const handleConfirm = async () => {
    if (isMessageEmpty || isMessageTooLong) {
      setIsMessageTouched(true);
      return false;
    }

    await dataProvider.customRequest(
      erasmusRequests.returnApplicationForCorrection(record.id),
      {
        fields: selectedChoices,
        message: trimmedMessage,
      },
      "POST",
    );

    notify("Успешно изпратена заявка за корекция.", { type: "success" });
    refresh();
    resetState();
  };

  const resetState = () => {
    setSelectedChoices([]);
    setMessage("");
    setIsMessageTouched(false);
  };

  const handleChange = id => {
    setSelectedChoices(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
  };

  return (
    <Box
      display="flex"
      justifyContent="flex-end"
    >
      <ConfirmModal
        disableBackdropClose={true}
        triggerButton={
          <Button
            variant="contained"
            color="inherit"
            size="medium"
            startIcon={<ModeEditIcon />}
            label="Върни документи за корекция"
          />
        }
        titleContent="Моля отбележете документите, които да бъдат върнати за корекция"
        saveTitle="Изпрати"
        closeTitle="Откажи"
        onConfirm={handleConfirm}
        resetProps={resetState}
        confirmEnabled={selectedChoices.length > 0 && !isMessageTooLong}
        dialogProps={{
          sx: {
            "& .MuiDialog-paper": {
              textAlign: "center",
              minWidth: "50%",
            },
            "& .MuiDialogContent-root": {
              overflow: "hidden !important",
            },
          },
        }}
      >
        <FormGroup
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: ".5rem",
            marginBottom: "1.5em",
            ...settings.style.shadowBox,
          }}
        >
          {documentFields(record).map(item =>
            (Array.isArray(item.value) ? item.value.length > 0 : Boolean(item.rawValue)) ? (
              <Labeled
                key={item.id}
                label={item.label}
                sx={{
                  textAlign: "left",
                  gridColumn: "auto",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedChoices.includes(item.id)}
                      onChange={() => handleChange(item.id)}
                    />
                  }
                  label={
                    item.isDocumentObject
                      ? item.value.map(doc => (
                          <DocumentFileLink
                            key={doc.documentId}
                            id={doc.documentId}
                            documentName={doc.name}
                          />
                        ))
                      : item.value
                  }
                />
              </Labeled>
            ) : null,
          )}
        </FormGroup>

        <TextInput
          label="Въведи съобщение"
          multiline
          variant="outlined"
          value={message}
          onChange={e => setMessage(e.target.value)}
          onBlur={() => setIsMessageTouched(true)}
          maxRows={6}
          minRows={4}
          fullWidth
          required
          error={isMessageTooLong || isMessageMissing}
          helperText={getMessageHelperText()}
        />
      </ConfirmModal>
    </Box>
  );
};

export default ReturnApplicationForCorrectionDialog;
